import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  api, getToken, setToken, setTenantCode, setUnauthorizedHandler,
  takeSessionEndedReason, clearSessionEndedReason,
} from './api'
import { applyBranding } from './branding'

/**
 * Session state: who is signed in, what they may do, and which organisation.
 *
 * Permissions come from the server on every session restore rather than being
 * cached, so a role revoked this morning takes effect on the next page load
 * instead of whenever a stale cache happens to expire.
 */

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [tenant, setTenant] = useState(null)

  // Distinct from "not signed in": until the token has been checked we do not
  // know which, and rendering the login screen in the meantime makes every
  // refresh flash a sign-in form at someone who is already signed in.
  //
  // Seeded to false immediately when there is no stored token at all — that
  // case needs no network round trip to resolve, so there is nothing to wait
  // out. Without this, visiting /login itself (which never had a token to
  // begin with) still showed the generic route-loading skeleton for a beat,
  // which is dashboard-shaped and reads as "wrong page loading" on a screen
  // that has no dashboard.
  const [restoring, setRestoring] = useState(() => Boolean(getToken()))

  /*
   * Why the last sign-out happened, so the login screen can say so.
   *
   * Being dropped to a sign-in form with no explanation is the single most
   * disorienting thing this app can do — the natural reading is "it broke", or
   * worse, "my password stopped working". Distinguishing an expired session
   * from a deliberate sign-out costs one string and removes that whole class of
   * confusion.
   */
  // Seeded from sessionStorage so it survives the hard page load that a 401
  // often arrives on. Read once and cleared — see takeSessionEndedReason().
  const [endedReason, setEndedReason] = useState(() => takeSessionEndedReason())

  const signOutLocally = useCallback((reason = null) => {
    setToken(null)
    setUser(null)
    setEndedReason(reason)

    // A deliberate sign-out must not leave an "your session ended" notice
    // behind for the next visit to the login screen.
    if (reason !== 'expired') clearSessionEndedReason()
  }, [])

  useEffect(() => {
    /*
     * Fired by the API client on any 401.
     *
     * `hadToken` says whether a token was actually presented. It is false for a
     * request that was already in flight when the user signed out — dropping
     * the session again is harmless, but calling that an expiry would greet
     * someone who just clicked "Sign out" with "your session ended".
     */
    setUnauthorizedHandler((hadToken) => {
      setToken(null)
      setUser(null)

      if (hadToken) setEndedReason('expired')
    })
  }, [])

  useEffect(() => {
    let cancelled = false

    async function restore() {
      if (!getToken()) {
        setRestoring(false)
        return
      }

      try {
        const data = await api.get('/auth/me')

        if (cancelled) return

        setUser(data.user)
        setTenant(data.tenant)
        applyBranding(data.tenant)
      } catch {
        // A failed restore means the stored token is dead. The interceptor has
        // already cleared it and recorded why, so the login screen can explain
        // itself rather than appearing for no visible reason.
        if (!cancelled) signOutLocally('expired')
      } finally {
        if (!cancelled) setRestoring(false)
      }
    }

    restore()

    return () => { cancelled = true }
  }, [signOutLocally])

  /*
   * Signs in with just a login (email or phone) and password — no
   * organisation code asked for up front. See AuthController::login() on
   * the backend: it searches every active tenant for a match, and only
   * comes back here asking which one when the SAME login+password is
   * genuinely valid in more than one (rare — the same person registered
   * separately in two client organisations). That case surfaces as
   * `organisation_selection_required: true` plus a list to choose from,
   * rather than a signed-in user — Login.jsx renders that list and calls
   * this again with `tenant` set to whichever the person picked.
   */
  const signIn = useCallback(async ({ login, password, tenant: tenantCode }) => {
    if (tenantCode) setTenantCode(tenantCode)

    const data = await api.post('/auth/login', {
      login,
      password,
      tenant: tenantCode || undefined,
      device: 'web',
    })

    if (data.organisation_selection_required) {
      return { organisationSelectionRequired: true, organisations: data.organisations }
    }

    setToken(data.token)
    setUser(data.user)
    setTenant(data.tenant)
    setTenantCode(data.tenant.code)
    applyBranding(data.tenant)
    setEndedReason(null)
    clearSessionEndedReason()

    return { user: data.user }
  }, [])

  const signOut = useCallback(async () => {
    /*
     * Local state is cleared BEFORE the server is told, not after.
     *
     * The obvious order — call /auth/logout, then clear — has a race. The
     * server deletes the token the moment it handles the request, so any
     * component still mounted (the dashboard polls several endpoints) can fire
     * a request in that window carrying a token the server has just destroyed.
     * That 401s, the interceptor records it as an expired session, and the
     * person who deliberately signed out is greeted with "your session ended,
     * you were signed out" as though something had gone wrong.
     *
     * Clearing first means no further request carries the token at all, so the
     * window does not exist. The server call is then best-effort: it revokes
     * the row, and if it fails the token expires on its own schedule anyway.
     */
    const revoking = getToken()

    signOutLocally('signed_out')

    if (!revoking) return

    try {
      await api.post('/auth/logout', null, {
        headers: { Authorization: `Bearer ${revoking}` },
      })
    } catch {
      // Best-effort. The user is already signed out locally, which is what
      // they asked for; the token expires on its own if this never lands.
    }
  }, [signOutLocally])

  const refresh = useCallback(async () => {
    const data = await api.get('/auth/me')
    setUser(data.user)
    setTenant(data.tenant)
    return data.user
  }, [])

  const value = useMemo(() => {
    const permissions = new Set(user?.permissions ?? [])
    const roles = new Set(user?.roles ?? [])
    const modules = new Set(tenant?.modules ?? [])

    return {
      user,
      tenant,
      restoring,
      signIn,
      signOut,
      refresh,

      // 'expired' | 'signed_out' | null — why the previous session ended, so
      // the login screen can explain itself.
      endedReason,

      isAuthenticated: Boolean(user),

      /** Does this person hold a permission? */
      can: (...codes) => codes.some((code) => permissions.has(code)),

      hasRole: (...keys) => keys.some((key) => roles.has(key)),

      /** Is a module licensed for this tenant? */
      hasModule: (module) => modules.size === 0 || modules.has(module),

      /*
       * Committee membership, which is NOT a permission.
       *
       * POSH and whistleblower access follows from appointment to the relevant
       * committee, and the server enforces exactly that. Mirroring the same
       * rule here means the navigation does not offer a link that would 404 —
       * but the server check is the real one; this only decides what to draw.
       */
      isIccMember: Boolean(user?.is_icc_member),
      isWhistleblowerMember: Boolean(user?.is_whistleblower_member),
      hasDisciplinaryCase: Boolean(user?.has_disciplinary_case),

      mustChangePassword: Boolean(user?.must_change_password),
    }
  }, [user, tenant, restoring, signIn, signOut, refresh, endedReason])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }

  return context
}
